import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "../../components/UI/Button";
import TextInput from "../../components/UI/TextInput";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Selection from "../../components/UI/Selection";
import { contentUseCase } from "../../../di/container";
import { useAuthStore } from "../../stores/auth-store";
import type { Faq } from "../../../domain/types/content";
import {
  FaqCategory,
  FAQ_CATEGORY_LABELS,
} from "../../../domain/types/content";

const FAQ_CATEGORIES: FaqCategory[] = [
  FaqCategory.ORDER_PAYMENT,
  FaqCategory.DELIVERY,
  FaqCategory.CANCEL_REFUND,
  FaqCategory.PRODUCT,
  FaqCategory.ETC,
];

type ViewMode = "list" | "detail" | "edit" | "create";

const FaqManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("전체");
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  // 폼 상태
  const [formData, setFormData] = useState<{
    category: FaqCategory;
    question: string;
    answer: string;
    isPublished: boolean;
  }>({
    category: FaqCategory.ORDER_PAYMENT,
    question: "",
    answer: "",
    isPublished: true,
  });

  // 데이터 로드
  useEffect(() => {
    if (user?.companyId) {
      loadFaqs();
    }
  }, [user?.companyId, selectedCategory]);

  const loadFaqs = async () => {
    if (!user?.companyId) return;

    setLoading(true);
    try {
      const category =
        selectedCategory === "전체" ? undefined : selectedCategory;
      const data = await contentUseCase.getFaqs(user.companyId, category);
      setFaqs(data);
    } catch (error) {
      console.error("FAQ 로드 실패:", error);
      alert("FAQ를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 검색 및 카테고리 필터링
  const filteredFaqs = useMemo(() => {
    return faqs.filter((faq) => {
      const matchesSearch =
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "전체" || faq.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [faqs, searchQuery, selectedCategory]);

  // FAQ 선택 (상세보기)
  const handleSelectFaq = (faq: Faq) => {
    setSelectedFaq(faq);
    setViewMode("detail");
  };

  // FAQ 수정 모드
  const handleEditFaq = (faq: Faq) => {
    setSelectedFaq(faq);
    setFormData({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      isPublished: faq.isPublished,
    });
    setViewMode("edit");
  };

  // FAQ 삭제
  const handleDeleteFaq = async (faqId: number) => {
    if (!window.confirm("정말 삭제하시겠습니까?") || !user?.companyId) return;

    try {
      await contentUseCase.deleteFaq(user.companyId, faqId);
      await loadFaqs();
      setViewMode("list");
      setSelectedFaq(null);
      alert("FAQ가 삭제되었습니다.");
    } catch (error) {
      console.error("FAQ 삭제 실패:", error);
      alert("FAQ 삭제에 실패했습니다.");
    }
  };

  // FAQ 생성 모드
  const handleCreateFaq = () => {
    setFormData({
      category: FaqCategory.ORDER_PAYMENT,
      question: "",
      answer: "",
      isPublished: true,
    });
    setSelectedFaq(null);
    setViewMode("create");
  };

  // 저장 (생성/수정)
  const handleSave = async () => {
    if (!user?.companyId) return;

    try {
      if (viewMode === "create") {
        const newFaq = await contentUseCase.createFaq(user.companyId, formData);
        await loadFaqs();
        setSelectedFaq(newFaq);
        setViewMode("detail");
        alert("FAQ가 생성되었습니다.");
      } else if (viewMode === "edit" && selectedFaq) {
        const updatedFaq = await contentUseCase.updateFaq(
          user.companyId,
          selectedFaq.id,
          formData,
        );
        await loadFaqs();
        setSelectedFaq(updatedFaq);
        setViewMode("detail");
        alert("FAQ가 수정되었습니다.");
      }
    } catch (error) {
      console.error("FAQ 저장 실패:", error);
      alert("FAQ 저장에 실패했습니다.");
    }
  };

  // 취소
  const handleCancel = () => {
    if (selectedFaq) {
      setViewMode("detail");
    } else {
      setViewMode("list");
    }
  };

  // 순서 변경
  const handleMoveUp = async (faq: Faq) => {
    if (!user?.companyId) return;

    const index = faqs.findIndex((f) => f.id === faq.id);
    if (index > 0) {
      try {
        const targetOrder = faqs[index - 1].displayOrder;
        await contentUseCase.updateFaqOrder(user.companyId, faq.id, {
          displayOrder: targetOrder,
        });
        await loadFaqs();
      } catch (error) {
        console.error("FAQ 순서 변경 실패:", error);
        alert("FAQ 순서 변경에 실패했습니다.");
      }
    }
  };

  const handleMoveDown = async (faq: Faq) => {
    if (!user?.companyId) return;

    const index = faqs.findIndex((f) => f.id === faq.id);
    if (index < faqs.length - 1) {
      try {
        const targetOrder = faqs[index + 1].displayOrder;
        await contentUseCase.updateFaqOrder(user.companyId, faq.id, {
          displayOrder: targetOrder,
        });
        await loadFaqs();
      } catch (error) {
        console.error("FAQ 순서 변경 실패:", error);
        alert("FAQ 순서 변경에 실패했습니다.");
      }
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="flex h-full bg-gray-50 overflow-hidden">
        {/* 좌측 목록 */}
        <div className="w-full lg:w-96 bg-white border-r border-gray-200 flex flex-col h-full">
          {/* 헤더 */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0 ">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-bold text-gray-900">FAQ 관리</h1>
              <Button
                onClick={handleCreateFaq}
                className="flex items-center gap-1 text-sm"
              >
                <Plus className="w-4 h-4" />
                작성
              </Button>
            </div>
            {/* 카테고리 필터 */}
            <div className="mb-3 h-[46px]">
              <Selection
                options={[
                  { value: "전체", name: "전체" },
                  ...FAQ_CATEGORIES.map((cat) => ({
                    value: cat,
                    name: FAQ_CATEGORY_LABELS[cat],
                  })),
                ]}
                selectedOption={selectedCategory}
                setSelectedOption={setSelectedCategory}
              />
            </div>
            {/* 검색 */}
            <div className="relative">
              <TextInput
                type="text"
                placeholder="검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-sm"
                inputClassName="pr-6"
              />
              <Search className="absolute z-1 right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* 목록 */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            {filteredFaqs.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                FAQ가 없습니다.
              </div>
            ) : (
              filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  onClick={() => handleSelectFaq(faq)}
                  className={`p-4 px-6 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedFaq?.id === faq.id
                      ? "bg-blue-50 border-l-4 border-l-blue-500"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mb-2">
                      {FAQ_CATEGORY_LABELS[faq.category]}
                    </span>
                    {faq.isPublished ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-2 flex-shrink-0">
                        공개
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-2 flex-shrink-0">
                        비공개
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1 text-left">
                    {faq.question}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2 text-left">
                    {faq.answer}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{faq.createdAt}</span>
                    <span>No. {faq.displayOrder}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 우측 상세/편집 영역 */}
        <div className={`${viewMode === "list" ? "hidden" : ""} h-100vh overflow-y-scroll lg:block absolute top-[116px] bg-white py-[25px] lg:top-auto left-[0px] md:left-[199px] lg:left-auto right-0 lg:right-auto bottom-0 lg:bottom-auto lg:relative  lg:bg-auto lg:flex-1 lg:overflow-y-auto lg:overflow-x-hidden lg:h-full`}>
          {viewMode === "list" && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-gray-300 mb-4 flex justify-center">
                  <svg
                    className="w-24 h-24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  FAQ를 선택하세요
                </h2>
                <p className="text-sm text-gray-500">
                  좌측 목록에서 FAQ를 선택하거나 새로 작성하세요
                </p>
              </div>
            </div>
          )}

          {viewMode === "detail" && selectedFaq && (
            <div className="px-8 w-full">
              <div className="w-full">
                {/* 헤더 */}
                <div className="flex items-start md:items-center flex-col md:flex-row gap-3 justify-between mb-6">
                  <div className="w-full flex flex-col lg:flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        role="button"
                        onClick={() => {
                          setViewMode("list");
                          setSelectedFaq(null);
                        }}
                      >
                        <svg
                          width="8"
                          height="12"
                          viewBox="0 0 8 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.12095 0.170067C7.32709 0.376209 7.34583 0.698787 7.17717 0.926099L7.12095 0.991223L2.11243 6L7.12095 11.0088C7.32709 11.2149 7.34583 11.5375 7.17717 11.7648L7.12095 11.8299C6.91481 12.0361 6.59223 12.0548 6.36492 11.8862L6.29979 11.8299L0.880438 6.41058C0.674296 6.20444 0.655556 5.88186 0.824217 5.65455L0.880438 5.58942L6.29979 0.170067C6.52655 -0.056689 6.89419 -0.056689 7.12095 0.170067Z"
                            fill="#252525"
                          />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 text-left">
                        {selectedFaq.question}
                      </h2>
                    </div>

                    <div className="w-full flex items-start flex-col md:flex-row gap-3">
                      <div className="flex gap-3">
     <span className="items-center justify-center px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800">
                        {FAQ_CATEGORY_LABELS[selectedFaq.category]}
                      </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleMoveUp(selectedFaq)}
                            className="p-1.5 hover:bg-gray-100 rounded border border-gray-300"
                            title="위로"
                          >
                            <ChevronUp className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleMoveDown(selectedFaq)}
                            className="p-1.5 hover:bg-gray-100 rounded border border-gray-300"
                            title="아래로"
                          >
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleEditFaq(selectedFaq)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          수정
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDeleteFaq(selectedFaq.id)}
                          className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          삭제
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 메타 정보 */}
                <div className="rounded-lg mb-6">
                  <div className="flex flex-col xl:flex-row items-start xl:items-center gap-2 xl:gap-6 text-sm text-left">
                    <div>
                      <span className="text-gray-500">작성자:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedFaq.author}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">작성일:</span>
                      <span className="ml-2 text-gray-900">
                        {selectedFaq.createdAt}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">공개여부:</span>
                      <span>
                        {selectedFaq.isPublished ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            공개
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            비공개
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 답변 */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap text-left">
                      {selectedFaq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(viewMode === "edit" || viewMode === "create") && (
            <div className="w-full px-8">
              <div className="w-full">
                {/* 헤더 */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {viewMode === "create" ? "FAQ 작성" : "FAQ 수정"}
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      취소
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!formData.question || !formData.answer}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      저장
                    </Button>
                  </div>
                </div>

                {/* 폼 */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      카테고리 <span className="text-red-500">*</span>
                    </label>
                    <div className="h-[46px]">
                      <Selection
                        options={FAQ_CATEGORIES.map((cat) => ({
                          value: cat,
                          name: FAQ_CATEGORY_LABELS[cat],
                        }))}
                        selectedOption={formData.category}
                        setSelectedOption={(value) =>
                          setFormData({
                            ...formData,
                            category: value as FaqCategory,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      질문 <span className="text-red-500">*</span>
                    </label>
                    <TextInput
                      type="text"
                      value={formData.question}
                      onChange={(e) =>
                        setFormData({ ...formData, question: e.target.value })
                      }
                      placeholder="질문을 입력하세요"
                      className="text-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      답변 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.answer}
                      onChange={(e) =>
                        setFormData({ ...formData, answer: e.target.value })
                      }
                      placeholder="답변을 입력하세요"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[300px] resize-y"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                      공개 여부
                    </label>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            isPublished: !formData.isPublished,
                          })
                        }
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          formData.isPublished ? "bg-blue-600" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.isPublished
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                      <span className="ml-3 text-sm text-gray-700">
                        {formData.isPublished ? "공개" : "비공개"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CompanyAdminLayout>
  );
};

export default FaqManagement;
